import get from 'lodash.get';

import isObject from '#shared/util/isObject.js';
import appError from '#shared/util/appError.js';
import readArguments from '#shared/util/readArguments.js';
import json from '#shared/util/json.js';

import avEvents from './avEvents.js';

/**
 * Use single object for all Mac applications
 */
window.Avastium = window.Avastium || window.securelineUI || get(window, 'webkit.messageHandlers.Avastium');

/**
 * Universal variables for checking Mac applications
 */
const isMacWKWebView = isObject(window.Avastium) && typeof window.Avastium.postMessage === 'function';
const isMacWebView = isObject(window.Avastium) && !isMacWKWebView;

/**
 * Wrapper function around Avastium WKWebView postMessage API
 * @param {string} method Avastium method name (eg. navigateInBrowser)
 * @param {object} [payload] Anything the Avastium method accepts
 * @param {string} [id] ID of the postMessage call to differentiate the request (will be generated if empty)
 * @param {Function} [callback] Callback when Avastium.postMessage returns data
 */
const avastiumPostMessage = (method, payload, id, callback) => {
	const messageId = typeof id === 'string' ? id : `${method}_${Date.now()}`;
	if (typeof callback === 'function') {
		window.addEventListener(method, (response) => {
			if (get(response, 'detail.id') === messageId) {
				callback(response.detail);
			}
		}, {
			once: true // The event listener will be removed once fired so we don't have N event listeners set on the window
		});
	}
	window.Avastium.postMessage(json.stringify({
		function: method,
		data: payload,
		id: messageId
	}));
};

/**
 * Polyfill for in-app browser window navigation
 * @param {object} options
 * @param {string} options.url
 */
if (isMacWKWebView || isMacWebView && typeof window.Avastium.navigateInBrowser === 'function') {
	window.OpenUrl = function(options) {
		if (isObject(options) && typeof options.url === 'string') {
			if (isMacWebView) {
				window.Avastium.navigateInBrowser(options.url);
			}
			if (isMacWKWebView) {
				avastiumPostMessage('navigateInBrowser', options.url);
			}
		} else {
			throw appError('Invalid method arguments to OpenUrl', {
				code: 'APP_FRONTEND_UTIL',
				origin: 'macPolyfill.OpenUrl',
				detail: readArguments(arguments) // @todo update detail structure
			});
		}
	};
}

/**
 * Polyfill for closing the current window
 */
if (isMacWKWebView || isMacWebView && typeof window.Avastium.closeWindow === 'function') {
	window['window.close'] = function() {
		if (isMacWebView) {
			window.Avastium.closeWindow();
		}
		if (isMacWKWebView) {
			avastiumPostMessage('closeWindow');
		}
	};
}

/**
 * Polyfill for being logged-in to account
 * @param {object} options
 */
if (isMacWKWebView || isMacWebView && typeof window.Avastium.loginToAccount === 'function') {
	window['app.lif.account.set_account_info'] = function(options) {
		if (isObject(options) && typeof options.email === 'string' && typeof options.tickets.NVAT === 'string') {
			if (isMacWebView) {
				window.Avastium.loginToAccount(options, () => {});
			}
			if (isMacWKWebView) {
				avastiumPostMessage('loginToAccount', options);
			}
		} else {
			throw appError('Invalid method arguments to loginByTicket', {
				code: 'APP_FRONTEND_UTIL',
				origin: 'macPolyfill.loginByTicket',
				detail: readArguments(arguments) // @todo update detail structure
			});
		}
	};
}

/**
 * Polyfill to get application logged-in account
 * @param {object} options
 */
if (isMacWKWebView || isMacWebView && typeof window.Avastium.getAccountInfo === 'function') {
	const appCall = 'app.lif.account.get_account_info';
	window[appCall] = function() {
		if (isMacWebView) {
			window.Avastium.getAccountInfo((response) => {
				avEvents.dispatchEvent(new Event(appCall), json.stringify(response || {}));
			});
		}
		if (isMacWKWebView) {
			avastiumPostMessage('getAccountInfo', null, null, (response) => {
				avEvents.dispatchEvent(new Event(appCall), json.stringify(response?.data || {}));
			});
		}
	};
}

/**
 * Polyfill for alpha activation
 * @param {string} code
 * @param {string} product
 * @param {string} event
 */
if (isMacWKWebView || isMacWebView && typeof window.Avastium.subscribeAlpha === 'function') {
	window.InsertWalletKey = function(code, product, event) {
		if (typeof code === 'string' && typeof product === 'string' && typeof event === 'string') {
			if (isMacWebView) {
				window.Avastium.subscribeAlpha((status) => {
					avEvents.dispatchEvent(new Event(event), `{"resultCode":${status === 0 ? '"0"' : '"1"'}}`);
				}, code, product);
			}
			if (isMacWKWebView) {
				avastiumPostMessage('subscribeAlpha', {
					wallet_key: code,
					correlation_id: product
				}, event, ({ data: status }) => {
					avEvents.dispatchEvent(new Event(event), `{"resultCode":${status === 0 ? '"0"' : '"1"'}}`);
				});
			}
		} else {
			throw appError('Invalid method arguments to InsertWalletKey', {
				code: 'APP_FRONTEND_UTIL',
				origin: 'macPolyfill.InsertWalletKey',
				detail: readArguments(arguments) // @todo update detail structure
			});
		}
	};
}

/**
 * Polyfill for legacy activation
 * @param {string} code
 * @param {string} product
 * @param {string} event
 */
if (isMacWKWebView || isMacWebView && typeof window.Avastium.subscribe === 'function') {
	window.ActivateProduct = function(code, product, event) {
		if (typeof code === 'string' && typeof product === 'string' && typeof event === 'string') {
			if (isMacWebView) {
				window.Avastium.subscribe((status) => {
					avEvents.dispatchEvent(new Event(event), `{"resultCode":${status === 0 ? '"0"' : '"1"'}}`);
				}, code, product);
			}
			if (isMacWKWebView) {
				avastiumPostMessage('subscribe', {
					code,
					SKU: product
				}, event, ({ data: status }) => {
					avEvents.dispatchEvent(new Event(event), `{"resultCode":${status === 0 ? '"0"' : '"1"'}}`);
				});
			}
		} else {
			throw appError('Invalid method arguments to ActivateProduct', {
				code: 'APP_FRONTEND_UTIL',
				origin: 'macPolyfill.ActivateProduct',
				detail: readArguments(arguments) // @todo update detail structure
			});
		}
	};
}

/**
 * Polyfill for calling window close event
 */
if (isMacWKWebView || isMacWebView && typeof window.Avastium.registerOnWindowClosed === 'function') {
	if (isMacWebView) {
		window.Avastium.registerOnWindowClosed(() => {
			if (typeof window.onWindowClosed === 'function') {
				window.onWindowClosed();
			}
		});
	}
	if (isMacWKWebView) {
		avastiumPostMessage('registerOnWindowClosed', null, null, () => {
			if (typeof window.onWindowClosed === 'function') {
				window.onWindowClosed();
			}
		});
	}
}
